/**
 * @file toast.store.ts
 * @description Zustand store backing the global Toaster. Tiny by design —
 *   push/dismiss + an auto-prune effect handled inside the component.
 *   No external toast library; we ship our own ~50-line one so the
 *   bundle stays lean and styling matches the brand palette.
 * @module Stores
 * @security Toast messages are user-visible by definition. NEVER call
 *   `notify.error(err.message)` directly with an ApiError — the message
 *   path goes through `lib/error-messages.ts` which maps server-side
 *   `problem.type`/`status`/`errors[].code` to safe i18n keys.
 */

import { create } from 'zustand';

export type ToastKind = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  kind: ToastKind;
  message: string;
  /** Milliseconds before auto-dismiss; 0 means sticky. */
  durationMs: number;
}

interface ToastState {
  toasts: Toast[];
  push: (kind: ToastKind, message: string, durationMs?: number) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

const DEFAULT_DURATIONS: Record<ToastKind, number> = {
  success: 3000,
  info: 4000,
  error: 6000,
};

let counter = 0;
function nextId(): string {
  counter += 1;
  return `t${counter}-${Date.now().toString(36)}`;
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (kind, message, durationMs) => {
    const id = nextId();
    const t: Toast = {
      id,
      kind,
      message,
      durationMs: durationMs ?? DEFAULT_DURATIONS[kind],
    };
    set((s) => ({ toasts: [...s.toasts, t] }));
    return id;
  },
  dismiss: (id) => {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
  clear: () => set({ toasts: [] }),
}));
